import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Loader2, ArrowRight, Home } from 'lucide-react';
import { getPaymentStatus } from '../services/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const isPaypal = searchParams.get('paypal') === 'true';
  const isCrypto = searchParams.get('crypto') === 'true';
  const subscriptionId = searchParams.get('subscription_id');
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkPaymentStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getPaymentStatus(sessionId);
      setStatus(result);
    } catch (err) {
      setError('Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const confirmPayPalSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API}/api/payments/paypal/confirm-subscription?subscription_id=${subscriptionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setStatus({ payment_status: 'paid', metadata: { tier: result.tier_id } });
      } else {
        setStatus({ payment_status: 'paid', metadata: {} });
      }
    } catch (err) {
      setStatus({ payment_status: 'paid', metadata: {} });
    } finally {
      setLoading(false);
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else if (isPaypal && subscriptionId) {
      confirmPayPalSubscription();
    } else if (isPaypal || isCrypto) {
      setStatus({ payment_status: 'paid', metadata: {} });
      setLoading(false);
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId, isPaypal, isCrypto, subscriptionId, checkPaymentStatus, confirmPayPalSubscription]);

  const isPaid = status?.payment_status === 'paid';
  const metadata = status?.metadata || {};

  return (
    <div className="min-h-screen bg-black text-gray-100 py-16 px-4" data-testid="payment-success-page">
      {/* Film grain overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      <div className="container mx-auto max-w-lg relative z-10">
        <Card className="bg-gray-900/90 border-2 border-amber-900/50">
          <CardHeader className="text-center border-b border-amber-900/30 pb-6">
            {loading ? (
              <>
                <Loader2 className="w-16 h-16 text-amber-500 mx-auto animate-spin mb-4" />
                <CardTitle className="font-headline text-2xl text-amber-500 tracking-wider">
                  VERIFYING PAYMENT
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Please wait while we confirm your transaction...
                </CardDescription>
              </>
            ) : error ? (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <CardTitle className="font-headline text-2xl text-red-500 tracking-wider">
                  VERIFICATION ERROR
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  {error}
                </CardDescription>
              </>
            ) : isPaid ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <CardTitle className="font-headline text-2xl text-green-500 tracking-wider">
                  PAYMENT SUCCESSFUL
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Your transaction has been confirmed
                </CardDescription>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <CardTitle className="font-headline text-2xl text-amber-500 tracking-wider">
                  PAYMENT PENDING
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Your payment is being processed
                </CardDescription>
              </>
            )}
          </CardHeader>

          {status && !loading && (
            <CardContent className="pt-6 space-y-6">
              {/* Transaction Details */}
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-amber-900/20">
                  <span className="text-gray-400 font-typewriter">Type</span>
                  <Badge className="bg-amber-900/30 text-amber-500 font-headline tracking-wider">
                    {metadata.type === 'subscription' ? 'SUBSCRIPTION' : 
                     metadata.type === 'post_purchase' ? 'POST PURCHASE' :
                     metadata.type === 'collection_purchase' ? 'COLLECTION' : 'PAYMENT'}
                  </Badge>
                </div>

                {metadata.tier_name && (
                  <div className="flex justify-between items-center py-2 border-b border-amber-900/20">
                    <span className="text-gray-400 font-typewriter">Tier</span>
                    <span className="text-amber-500 font-headline tracking-wider">{metadata.tier_name}</span>
                  </div>
                )}

                {metadata.post_title && (
                  <div className="flex justify-between items-center py-2 border-b border-amber-900/20">
                    <span className="text-gray-400 font-typewriter">Content</span>
                    <span className="text-gray-200 font-typewriter text-sm">{metadata.post_title}</span>
                  </div>
                )}

                {metadata.collection_title && (
                  <div className="flex justify-between items-center py-2 border-b border-amber-900/20">
                    <span className="text-gray-400 font-typewriter">Collection</span>
                    <span className="text-gray-200 font-typewriter text-sm">{metadata.collection_title}</span>
                  </div>
                )}

                <div className="flex justify-between items-center py-2 border-b border-amber-900/20">
                  <span className="text-gray-400 font-typewriter">Amount</span>
                  <span className="text-green-500 font-headline text-xl tracking-wider">
                    ${status.amount_total?.toFixed(2)} {status.currency?.toUpperCase()}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400 font-typewriter">Status</span>
                  <Badge 
                    className={`font-headline tracking-wider ${
                      isPaid ? 'bg-green-900/30 text-green-500' : 'bg-amber-900/30 text-amber-500'
                    }`}
                  >
                    {status.payment_status?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-4">
                {isPaid && metadata.type === 'subscription' && (
                  <Button
                    onClick={() => navigate('/feed')}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider w-full"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    ACCESS EXCLUSIVE CONTENT
                  </Button>
                )}

                {isPaid && metadata.type === 'post_purchase' && (
                  <Button
                    onClick={() => navigate(`/post/${metadata.post_id}`)}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider w-full"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    VIEW PURCHASED POST
                  </Button>
                )}

                {isPaid && metadata.type === 'collection_purchase' && (
                  <Button
                    onClick={() => navigate(`/collection/${metadata.collection_id}`)}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider w-full"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    VIEW COLLECTION
                  </Button>
                )}

                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider w-full"
                >
                  <Home className="w-4 h-4 mr-2" />
                  RETURN HOME
                </Button>
              </div>
            </CardContent>
          )}

          {error && (
            <CardContent className="pt-6">
              <Button
                onClick={() => navigate('/tiers')}
                className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider w-full"
              >
                TRY AGAIN
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
